import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  const checkAuth = async () => {
    setIsLoading(true);
    try {
      // Check for session cookie before making request
      const hasSessionCookie = document.cookie.split(';').some(cookie =>
        cookie.trim().startsWith('sessionid=') || cookie.trim().startsWith('__Secure-sessionid=')
      );
      console.log('Session cookie present:', hasSessionCookie);

      const response = await authService.checkAuth();
      console.log('Auth check response:', response.data);

      // The backend now always returns 200, with isAuthenticated true/false in response
      if (response && response.data && response.data.isAuthenticated === true) {
        setIsAuthenticated(true);
        setUser(response.data.user || null);
        console.log('User authenticated:', response.data.user);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        console.log('User not authenticated');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // If check fails (network error, etc), assume not authenticated
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // On mount, check if user is already authenticated via session cookie.
    // Intentionally NOT re-running this on window focus: flipping isLoading
    // back to true on every focus event causes every route guard
    // (AdminRoute, PrivateRoute, ProtectedRoute) to unmount its children
    // and render a loading spinner, which wipes out open modals and any
    // in-memory state every time the user alt-tabs. The 401 interceptor in
    // services/api.js already redirects to /login on a stale session, so a
    // preemptive focus-refetch is redundant.
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      // Use the user data from the login response
      if (response && response.data && response.data.user) {
        setIsAuthenticated(true);
        setUser(response.data.user);
        return true;
      } else {
        // If response doesn't have user data, call checkAuth to verify
        const authCheck = await authService.checkAuth();
        if (authCheck && authCheck.data && authCheck.data.isAuthenticated) {
          setIsAuthenticated(true);
          setUser(authCheck.data.user);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const updateUser = (partial) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : null));
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
